"use client";

import { useState } from "react";

import { DecisionMetadata } from "@/configuration/groups/tpr/components/referral-instructions";
import {
  buildTprReferralAuthInstruction,
  formatTprReferralAuthSkipToast,
  formatTprReferralAuthWriteToast,
  getTprReferralAuthOpenButtonTooltip,
} from "@/configuration/groups/tpr/referral-auth-instructions";
import { submitTprReferralAuthorization } from "@/src/actions/tpr-referral-auth";
import { CopyableContent } from "@/src/components/copyable-content";
import { ReferralAuthInstructionsCard } from "@/src/components/tasks/referral-auth-instructions-card";
import { ReferralEligibilityCard } from "@/src/components/tasks/referral-eligibility-card";
import { ValerieCard } from "@/src/components/valerie-card";
import { toast } from "@/src/hooks/use-toast";
import type { TprReferralAuthInstructionContext } from "@/src/lib/customer/tpr/referral-auth-instruction-context";
import { logger } from "@/src/lib/logging/logger";
import { resolveReferralAuthOnFile } from "@/src/lib/prior-authorization/referral-auth-on-file";
import { formatDateMmDdYyyy } from "@/src/lib/utils/date-and-phone-utils";
import type { PriorAuthorization } from "@/src/models/documents/prior-authorization/prior-authorization";
import { Referral } from "@/src/models/documents/referral";
import { getPractitionerNameWithCredentials } from "@/src/models/practitioner";

interface TprReferralAuthInstructionsProps extends TprReferralAuthInstructionContext {
  referral: Referral;
  linkedPriorAuthorizations: PriorAuthorization[];
  taskId: string;
  taskPriorAuthorization?: PriorAuthorization | null;
  startTrackingCaseDisabled?: boolean;
}

export function TprReferralAuthInstructions({
  referral,
  linkedPriorAuthorizations,
  taskId,
  taskPriorAuthorization,
  startTrackingCaseDisabled = false,
  createdAthenaReferralAuthId,
  hasWritableAthenaInsurance,
  lookup,
}: TprReferralAuthInstructionsProps) {
  const [isOpening, setIsOpening] = useState(false);
  const [athenaReferralAuthId, setAthenaReferralAuthId] = useState(createdAthenaReferralAuthId);
  const authOnFile = resolveReferralAuthOnFile(referral, linkedPriorAuthorizations, taskPriorAuthorization);
  const isButtonDisabled =
    startTrackingCaseDisabled || isOpening || !hasWritableAthenaInsurance || Boolean(athenaReferralAuthId);
  const referringProviderName = referral.referringProvider
    ? getPractitionerNameWithCredentials(referral.referringProvider)
    : "N/A";
  const dateOfReferral = formatDateMmDdYyyy(referral.dateOfReferral) || "N/A";
  const referralAuthRequestedNote = `Valerie Health: Referral Authorization Requested ${dateOfReferral}`;
  const referralAuthNoteFieldText = "Request for referral authorization.";

  const handleOpenAuthorization = async () => {
    setIsOpening(true);

    try {
      const result = await submitTprReferralAuthorization({ taskId });

      if (result.kind === "skipped") {
        toast(formatTprReferralAuthSkipToast(result.reason));

        return;
      }

      setAthenaReferralAuthId(result.referralAuthId);
      toast(formatTprReferralAuthWriteToast(result.write, result.alreadyExisted));
    } catch (error) {
      logger.error("Failed to submit TPR referral authorization", { error, taskId });
      toast({
        title: "Could not submit authorization",
        description: error instanceof Error ? error.message : "Could not write the authorization to Athena.",
        variant: "destructive",
      });
    } finally {
      setIsOpening(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <ReferralAuthInstructionsCard
        appearance="standard"
        instruction={buildTprReferralAuthInstruction({
          authOnFile,
          createdAthenaReferralAuthId: athenaReferralAuthId,
          hasWritableAthenaInsurance,
          lookup,
        })}
        openButtonTooltip={getTprReferralAuthOpenButtonTooltip({
          authOnFile,
          createdAthenaReferralAuthId: athenaReferralAuthId,
          hasWritableAthenaInsurance,
          isSubmitting: startTrackingCaseDisabled,
          lookup,
        })}
        isOpenButtonDisabled={isButtonDisabled}
        isOpening={isOpening}
        onOpenAuthorization={handleOpenAuthorization}
      />

      {lookup.need === "not_required" && (
        <ValerieCard title="Referral Authorization Not Required">
          <div className="space-y-3 text-sm text-muted-foreground">
            <ol className="list-decimal list-inside space-y-1">
              <li>Double-check that referral and authorization are not required before marking complete.</li>
              <li>Mark sync complete.</li>
            </ol>
          </div>
        </ValerieCard>
      )}

      {lookup.need === "paper_referral" && (
        <ValerieCard title="Paper Referral Required">
          <div className="space-y-3 text-sm text-muted-foreground">
            <ol className="list-decimal list-inside space-y-1">
              <li>
                {athenaReferralAuthId
                  ? "Referral object has been written to Athena (PAPER, not required)."
                  : 'Click "Open Authorization" above to write a Referral object (PAPER, not required) to Athena.'}
              </li>
              <li>Mark sync complete.</li>
            </ol>
          </div>
        </ValerieCard>
      )}

      {lookup.need === "authorization" && (
        <ValerieCard title="Outbound Request for Referral Authorization">
          <div className="space-y-3 text-sm text-muted-foreground">
            <DecisionMetadata
              items={[
                { label: "Authorization Number", value: authOnFile.authorizationNumber || "N/A" },
                { label: "Start Date", value: authOnFile.startDate || "N/A" },
                { label: "End Date", value: authOnFile.endDate || "N/A" },
                { label: "Number of Services", value: authOnFile.visits || "N/A" },
              ]}
            />
            {/* TPR has no automated outbound-fax capability (see referral-auth/capabilities.ts). The
                Athena write creates the authorization object only. Ops must export the chart and send
                the request. The letter is not uploaded automatically. */}
            <ol className="list-decimal list-inside space-y-2">
              <li>
                {athenaReferralAuthId
                  ? "Authorization has been written to Athena (see above)."
                  : 'Click "Open Authorization" above to write the authorization to Athena.'}
              </li>
              <li>
                Request letter
                <ul className="ml-4 mt-1 space-y-1 list-disc list-inside">
                  <li>
                    The request letter is not uploaded automatically. On the referral task, use Upload request letter
                    after exporting the chart.
                  </li>
                  <li>Open the uploaded document and verify everything matches what&apos;s being requested.</li>
                </ul>
              </li>
              <li>
                Chart Export — sending to referring provider
                <ul className="ml-4 mt-1 space-y-1 list-disc list-inside">
                  <li>Click the hamburger menu (3 dashes) → select Chart Export.</li>
                  <li>Click the + button next to Admin Documents.</li>
                  <li>
                    Find the &quot;Request for Authorization Referral&quot; doc and move it to the right-hand side.
                  </li>
                  <li>
                    Click Export Chart Sections → send to <CopyableContent text={referringProviderName} />
                  </li>
                </ul>
              </li>
              <li>
                Fill out export details
                <ul className="ml-4 mt-1 space-y-1 list-disc list-inside">
                  <li>
                    Type <CopyableContent text={referringProviderName} /> — select from the dropdown once it populates.
                  </li>
                  <li>Confirm method is set to Athena Fax and the fax number has populated.</li>
                  <li>
                    Attention box: <CopyableContent text={referringProviderName} />
                  </li>
                  <li>
                    Note field: <CopyableContent text={referralAuthNoteFieldText} />
                  </li>
                  <li>Confirm Submit, then Save.</li>
                  <li>
                    Add note to patient chart: <CopyableContent text={referralAuthRequestedNote} />
                  </li>
                </ul>
              </li>
              <li className="text-xs italic">
                Caution: starting Chart Export automatically creates another document instance in the patient&apos;s
                chart. Only run Chart Export once per request to avoid creating duplicates.
              </li>
            </ol>
          </div>
        </ValerieCard>
      )}

      <ReferralEligibilityCard referralId={referral.id} taskId={taskId} />
    </div>
  );
}
